import { constants } from '../config';

const GOOGLEMAP_URL = 'https://maps.googleapis.com/maps/api/js';
const WINDOW_READY_CALLBACK_NAME = 'mapReadyFunction';
const WINDOW_SUCCESS_CALLBACK_NAME = 'mapSuccessFunction';
const WINDOW_ERROR_CALLBACK_NAME = 'mapErrorFunction';
const WINDOW_EXPIRY_CALLBACK_NAME = 'mapExpiryFunction';
const ZOOM = 14;

/*
  This class handles google map.
*/
class GoogleMap {
  constructor() {
    this.id = null;
    this.key = constants.googleMapsAPIKey;
    this.ready = false;
    this.readyCallbacks = [];
    this.successCallbacks = [];
    this.expiryCallbacks = [];
    this.errorCallbacks = [];
    this._handleLoad = this._handleLoad.bind(this);
    this._runSuccessCallbacks = this._runSuccessCallbacks.bind(this);
    this._runErrorCallbacks = this._runErrorCallbacks.bind(this);
    this._runExpiryCallbacks = this._runExpiryCallbacks.bind(this);
    // Create google map script
    const head = document.getElementsByTagName('head').item(0);
    const script = document.createElement('script');
    script.setAttribute('type', 'text/javascript');
    script.setAttribute('src', `${GOOGLEMAP_URL}?key=${this.key}&callback=${WINDOW_READY_CALLBACK_NAME}`);
    head.appendChild(script);
    // Create style
    const style = document.createElement('style');
    style.setAttribute('type', 'text/css');
    const styles = `
      .map-button {
        background-color: #fff;
        border: 1px solid #ccc;
        padding: 10px;
        cursor: pointer;
        font-size: 14px;
        border-radius: 3px;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
        z-index: 5;
        margin-top: 10px;
        margin-left: 10px;
      }
    `;
    style.innerHTML = styles;
    head.appendChild(style);

    // Set callbacks on window
    window[WINDOW_READY_CALLBACK_NAME] = this._handleLoad;
    window[WINDOW_SUCCESS_CALLBACK_NAME] = this._runSuccessCallbacks;
    window[WINDOW_ERROR_CALLBACK_NAME] = this._runErrorCallbacks;
    window[WINDOW_EXPIRY_CALLBACK_NAME] = this._runExpiryCallbacks;
  }

  _handleLoad() {
    // Remove from window
    delete window[WINDOW_READY_CALLBACK_NAME];
    this.ready = true;
    this.readyCallbacks.forEach(callback => callback());
    this.readyCallbacks = [];
  }

  _runSuccessCallbacks(token) {
    this.successCallbacks.forEach(callback => callback(token));
  }

  _runErrorCallbacks() {
    this.errorCallbacks.forEach(callback => callback());
  }

  _runExpiryCallbacks() {
    this.expiryCallbacks.forEach(callback => callback());
  }

  render(elementId, center, modeEdit) {
    const mapOptions = {
      center,
      zoom: ZOOM,
      fullscreenControl: false,
      mapTypeControl: false,
      scrollwheel: false,
      streetViewControl: false,
      zoomControl: true,
      zoomControlOptions: {
        position: window.google.maps.ControlPosition.RIGHT_TOP,
      },
      gestureHandling: 'auto',
      draggableCursor: 'default',
    };

    this.map = new window.google.maps.Map(document.getElementById(elementId), mapOptions);

    if (modeEdit) {
      if (this.marker) {
        this.marker.setMap(null);
      }

      // eslint-disable-next-line no-undef
      this.marker = new google.maps.Marker({
        position: { lat: center.lat, lng: center.lng },
        map: this.map,
      });
    }

    this.map.addListener('dragstart', () => {
      this.map.setOptions({
        draggingCursor: 'crosshair',
      });
    });

    this.map.addListener('dragend', () => {
      this.map.setOptions({
        draggingCursor: 'default',
      });
    });

    this.map.addListener('click', (event) => {
      if (this.marker) {
        this.marker.setMap(null);
      }

      // eslint-disable-next-line no-undef
      this.marker = new google.maps.Marker({
        position: event.latLng,
        map: this.map,
      });

      // eslint-disable-next-line prefer-destructuring
      const latLng = event.latLng;
      const eventDetail = {
        lat: latLng.lat(),
        lng: latLng.lng(),
      };
      this.successCallbacks[0](eventDetail);
    });

    const locateButtonDiv = document.createElement('div');
    locateButtonDiv.classList.add('map-button');
    const locateButton = document.createElement('button');
    locateButton.classList.add('map-button');
    locateButton.innerHTML = 'Mi ubicación';
    locateButtonDiv.appendChild(locateButton);

    locateButton.addEventListener('click', (event) => {
      event.preventDefault();
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;

            this.map.setCenter(new window.google.maps.LatLng(lat, lng));
            this.map.setZoom(ZOOM);

            if (this.marker) {
              this.marker.setMap(null);
            }
            this.marker = new window.google.maps.Marker({
              position: { lat, lng },
              map: this.map,
            });
            const eventDetail = {
              lat,
              lng,
            };
            this.successCallbacks[0](eventDetail);
          },
          (error) => {
            alert(`No se pudo obtener la ubicación: ${error.message}`);
          },
          {
            enableHighAccuracy: true, // Intentar usar GPS si está disponible
            timeout: 5000, // Tiempo máximo de espera para obtener la ubicación (en ms)
            maximumAge: 0, // No usar información de ubicación almacenada
          },
        );
      } else {
        alert('La geolocalización no es compatible con este navegador.');
      }
    });

    this.map.controls[window.google.maps.ControlPosition.TOP_LEFT].push(locateButton);
  }

  onReady(callback) {
    if (!this.ready) {
      this.readyCallbacks.push(callback);
    } else {
      callback();
    }
  }

  onSuccess(callback) {
    this.successCallbacks.push(callback);
  }

  offSuccess(callback) {
    this.successCallbacks = this.successCallbacks.filter(c => callback !== c);
  }

  onError(callback) {
    this.errorCallbacks.push(callback);
  }

  offError(callback) {
    this.errorCallbacks = this.errorCallbacks.filter(c => callback !== c);
  }

  onExpiry(callback) {
    this.expiryCallbacks.push(callback);
  }

  offExpiry(callback) {
    this.expiryCallbacks = this.expiryCallbacks.filter(c => callback !== c);
  }
}

const googleMap = new GoogleMap();

export { googleMap as GoogleMap };



// WEBPACK FOOTER //
// ./src/helpers/GoogleMap.js